// libs
import React, { memo } from 'react';
import { useHistory } from 'react-router';

// components
import { COUPON_TYPE } from 'shared/layouts/Coupons/types';
import { COUPON_CLICK_LOCATION_TYPE } from 'shared/layouts/Coupons/constants';
import ViewDetailsNudgeDesktopOffer from 'shared/layouts/Coupons/ViewDetailsNudge/ViewDetailsNudgeOffers';
import { SHEET_TRANSITION } from 'shared/components/OverlaySheet';

// constants
import { EMPTY_COUPON_DATA, TEXTS } from 'shared/layouts/Coupons/constants';
import { OFFER_PROPERTIES, PLP_REDIRECT_URL } from './constants';

// styles
import {
  ActionContainerDetails,
  ContainerWrapper,
  ViewDetailsNudgeDesktopCoupon,
  ViewDetailsNudgeMobileNew,
  ViewDetailsNudgeMobileOffer,
} from './styles';

import { ActionButtonCoupon, CopyButtonPDP } from 'shared/layouts/Coupons/ViewDetailsNudge/styles';

// hook
import useFetchCouponDetails from './useFetchCouponDetails';

// analytics
import { onCouponShopMoreClicked } from 'shared/layouts/Coupons/analytics';
import { onViewProductsClicked } from './analytics';

export enum PreviewFrom {
  PDP = 'pdp',
  BestPrice = 'bestPrice',
}

enum OFFERS_CTA {
  BACK = 'Back',
  OKAY = 'Okay',
}

interface CouponOfferDetailsProps {
  isDesktop?: boolean;
  // eslint-disable-next-line  no-undef
  showDetailCoupon: ProductOffers.ShowDetailCoupon;
  setCopySuccessMessage: (message: string) => void;
  // eslint-disable-next-line  no-undef
  setshowDetailCoupon: (couponDetailsToShow: ProductOffers.ShowDetailCoupon) => void;
  onCopyClicked: (
    code: string,
    // eslint-disable-next-line  no-undef
    coupon: ProductOffers.ShowDetailCoupon,
    isDetailsBottomSheet?: boolean
  ) => void;
  className?: string;
  sheetTransition?: SHEET_TRANSITION;
  previewFrom?: PreviewFrom;
  setParentSheetHeight?: (height: number) => void | null;
  showOverlay?: boolean;
  isEligibleForDeltaCommunication?: boolean;
}

function CouponOfferDetails({
  isDesktop = false,
  showDetailCoupon,
  setCopySuccessMessage,
  setshowDetailCoupon,
  onCopyClicked,
  className = '',
  sheetTransition = SHEET_TRANSITION.BOTTOM,
  previewFrom = PreviewFrom.PDP,
  setParentSheetHeight,
  showOverlay = true,
  isEligibleForDeltaCommunication = false,
}: CouponOfferDetailsProps) {
  const resetShowDetails = () =>
    setshowDetailCoupon({
      showDetails: false,
      coupon: EMPTY_COUPON_DATA,
      disabled: false,
      type: '',
    });

  const history = useHistory();
  const { currentCouponDetails, currentOfferDetails } = useFetchCouponDetails(showDetailCoupon);

  const onHide = () => {
    resetShowDetails();
    setCopySuccessMessage('');
  };

  const onGotoProductsClicked = () => {
    const offerId = showDetailCoupon?.coupon.offerId;
    onViewProductsClicked({
      pageType: 'coupon',
      offerId,
      couponClickType:
        showDetailCoupon.type == COUPON_TYPE.COUPON
          ? COUPON_CLICK_LOCATION_TYPE.PDP_COUPONS_WIDGET_SEE_DETAILS
          : COUPON_CLICK_LOCATION_TYPE.PDP_OFFERS_WIDGET_SEE_DETAILS,
    });
    onCouponShopMoreClicked(showDetailCoupon?.coupon, isEligibleForDeltaCommunication, true);
    const url = `${PLP_REDIRECT_URL}/${offerId}?coupon_offer_id=${offerId}&couponId=${showDetailCoupon?.coupon?.couponDetails?.couponCode}`;
    history.push(url);
  };

  const getCouponDetailActionContainer = (doHide: () => void) => {
    return (
      <ActionContainerDetails>
        {currentCouponDetails?.deltaTextModified && isEligibleForDeltaCommunication ? (
          <>
            <ActionButtonCoupon
              kind={'secondary'}
              size={'medium'}
              shape={'default'}
              onClick={() =>
                onCopyClicked(
                  currentCouponDetails?.couponCode || '',
                  showDetailCoupon?.coupon,
                  true
                )
              }
            >
              {TEXTS.COPY_CODE}
            </ActionButtonCoupon>
            <CopyButtonPDP
              onClick={() =>
                onGotoProductsClicked(currentCouponDetails?.couponCode || '', currentCouponDetails)
              }
              disabled={false}
            >
              {TEXTS.VIEW_PRODUCTS}
            </CopyButtonPDP>
          </>
        ) : (
          <>
            <ActionButtonCoupon
              kind={'secondary'}
              size={'medium'}
              shape={'default'}
              onClick={doHide}
            >
              {previewFrom === PreviewFrom.BestPrice ? TEXTS.BACK : TEXTS.DISMISS}
            </ActionButtonCoupon>
            <CopyButtonPDP
              onClick={() =>
                onCopyClicked(currentCouponDetails?.couponCode || '', currentCouponDetails, true)
              }
              disabled={false}
            >
              {TEXTS.COPY_CODE}
            </CopyButtonPDP>
          </>
        )}
      </ActionContainerDetails>
    );
  };

  return (
    <>
      <ContainerWrapper data-test="container-coupon" parentOverflow={!isDesktop}>
        {showDetailCoupon.showDetails ? (
          isDesktop ? (
            <>
              {showDetailCoupon.type === COUPON_TYPE.COUPON && (
                <ViewDetailsNudgeDesktopCoupon
                  couponDetails={currentCouponDetails}
                  onHide={onHide}
                  onCopyClicked={onCopyClicked}
                  className={className}
                />
              )}
              {showDetailCoupon.type === COUPON_TYPE.OFFER && (
                <ViewDetailsNudgeDesktopOffer offerDetails={currentOfferDetails} onHide={onHide} />
              )}
            </>
          ) : (
            <>
              {showDetailCoupon.type === COUPON_TYPE.COUPON && (
                <ViewDetailsNudgeMobileNew
                  couponDetails={currentCouponDetails}
                  onHide={onHide}
                  isApplied={false}
                  logoHeight={OFFER_PROPERTIES.PREVIEW_LOGO_HEIGHT}
                  logoWidth={OFFER_PROPERTIES.PREVIEW_LOGO_WIDTH}
                  actionContainer={(doHide) => getCouponDetailActionContainer(doHide)}
                  className={className}
                  sheetTransition={sheetTransition}
                  showBackButton={previewFrom === PreviewFrom.BestPrice}
                  setParentSheetHeight={setParentSheetHeight}
                  showOverlay={showOverlay}
                  isEligibleForDeltaCommunication={isEligibleForDeltaCommunication}
                />
              )}
              {showDetailCoupon.type === COUPON_TYPE.OFFER && (
                <ViewDetailsNudgeMobileOffer
                  offerDetails={currentOfferDetails}
                  onHide={onHide}
                  className={className}
                  sheetTransition={sheetTransition}
                  actionContainerText={
                    previewFrom === PreviewFrom.BestPrice ? OFFERS_CTA.BACK : OFFERS_CTA.OKAY
                  }
                  showBackButton={previewFrom === PreviewFrom.BestPrice}
                  showOverlay={showOverlay}
                  isEligibleForDeltaCommunication={isEligibleForDeltaCommunication}
                />
              )}
            </>
          )
        ) : null}
      </ContainerWrapper>
    </>
  );
}

export default memo(CouponOfferDetails);
